// Hooks that have UI dependencies and remain in components layer
import { useAtom, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import {
  googleUserAtom,
  getFileInfo,
  getSpreadsheetResponse,
  getSheetTab,
  GDriveSpreadsheetT,
} from 'infrastructure/googleDrive'
import { useCallback } from 'react'
import { globalDialogAtom } from 'shared/ui/GlobalDialog'
import { getGoogleDriveFolderId } from 'infrastructure/googleDrive/urlParser'

export function useGetFileInfo() {
  const [user, resetUser] = useAtom(googleUserAtom)

  return useCallback(
    async (folderUrl: string) => {
      if (!user.credential?.accessToken) return ''
      const folderId = getGoogleDriveFolderId(folderUrl)
      const resp = await getFileInfo(user.credential.accessToken, folderId)
      if ('error' in resp && resp.error.code === 401) {
        resetUser()
        return ''
      }
      return resp
    },
    [user, resetUser],
  )
}

export function useGetSpreadsheet() {
  const [guser, resetGUser] = useAtom(googleUserAtom)
  const setDialog = useSetAtom(globalDialogAtom)

  return useCallback(
    async (sheetId: string): Promise<GDriveSpreadsheetT | undefined> => {
      if (!guser.credential?.accessToken) return
      const response = await getSpreadsheetResponse(
        guser.credential.accessToken,
        sheetId,
      )
      if (!response.ok || response.status >= 400) {
        resetGUser()
        setDialog({
          title: 'Error getting Google spreadsheet',
          contents: ['Resetting Google Session'],
        })
        return
      }
      return response.json()
    },
    [guser, resetGUser, setDialog],
  )
}

export function useGetSheetTab() {
  const [guser, resetGUser] = useAtom(googleUserAtom)
  const setDialog = useSetAtom(globalDialogAtom)
  return useCallback(
    async (fileId: string, tabId?: string) => {
      if (!guser.credential?.accessToken) return ''
      const { result, error } = await getSheetTab({
        token: guser.credential.accessToken,
        fileId,
        tabId,
      })
      if (error) {
        resetGUser()
        setDialog({
          title: 'Error getting Google spreadsheet',
          contents: ['Resetting Google Session'],
        })
      }
      return result
    },
    [guser, resetGUser, setDialog],
  )
}
